<template>
  <div>
    <el-row :gutter="15">
      <el-col :span="22">
        <el-card
          v-loading="isLoadingEvents || isLoadingStatusLogs"
          shadow="never"
          class="mb-3 text-center"
        >
          <calendar-heatmap
            :values="currentEvents"
            :end-date="currentEndDate"
            tooltip-unit="Events"
            style="max-width: 1000px;"
            @day-click="(date) => dateSelected = date.date"
          />
        </el-card>
        <el-row
          v-loading="isLoadingEvents || isLoadingStatusLogs"
          :gutter="15"
        >
          <el-col :md="8">
            <el-card shadow="never">
              <div slot="header">
                <span>Activity Overview</span>
              </div>
              <!-- Status Logs -->
              <h3 v-if="!$_.isEmpty(currentYearStatusLogs)">
                Status Logs
              </h3>
              <ul v-if="!$_.isEmpty(currentYearStatusLogs)">
                <li
                  v-for="log in currentYearStatusLogs"
                  :key="log.id"
                  class="mb-1"
                >
                  <p>
                    <i style="color: gray;">{{ $moment(log.created_at).format('llll') }}</i>
                    <br>
                    Status Changed From <b>{{ $filters.titleize(log.status_from) }}</b> To <b>{{ $filters.titleize(log.status_to) }}</b>
                  </p>
                </li>
              </ul>
              <!-- Events -->
              <h3 v-if="!$_.isEmpty(currentYearEvents)">
                Completed Events
              </h3>
              <ul v-if="!$_.isEmpty(currentYearEvents)">
                <li
                  v-for="event in currentYearEvents"
                  :key="event.id"
                  class="mb-1"
                >
                  <p>
                    <i style="color: gray;">{{ $moment(event.completed_at).format('llll') }}</i>
                    <br>
                    {{ $_.get(event, 'type.name', '-') }}
                  </p>
                </li>
              </ul>
              <p v-if="$_.isEmpty(currentYearStatusLogs) && $_.isEmpty(currentYearEvents)">
                <i>None</i>
              </p>
            </el-card>
          </el-col>
          <el-col :md="8">
            <el-card shadow="never">
              <div slot="header">
                <span>Events Distribution</span>
              </div>
              <div style="display: flex; justify-content: center;">
                <VueChart
                  v-if="!isLoadingEvents || !isLoadingStatusLogs"
                  ref="chart"
                  chart-type="Radar"
                  :chart-data="chartData"
                  :options="chartOptions"
                  style="max-width: 400px;"
                />
              </div>
            </el-card>
          </el-col>
          <el-col :md="8">
            <el-card shadow="never">
              <p><i style="color: gray;">{{ $moment(dateSelected).format('llll') }}</i></p>
              <div slot="header">
                <span>Events of the selected day</span>
              </div>
              <!-- Status Logs -->
              <h3 v-if="!$_.isEmpty(selectedDateStatusLogs)">
                Status Logs
              </h3>
              <ul v-if="!$_.isEmpty(selectedDateStatusLogs)">
                <li
                  v-for="log in selectedDateStatusLogs"
                  :key="log.id"
                  class="mb-1"
                >
                  <p>
                    <i style="color: gray;">{{ $moment(log.created_at).format('hh:mm A') }}</i>
                    <br>
                    Status Changed From <b>{{ $filters.titleize(log.status_from) }}</b> To <b>{{ $filters.titleize(log.status_to) }}</b>
                  </p>
                </li>
              </ul>
              <!-- Events -->
              <h3 v-if="!$_.isEmpty(selectedDateEvents)">
                Completed Events
              </h3>
              <ul v-if="!$_.isEmpty(selectedDateEvents)">
                <li
                  v-for="event in selectedDateEvents"
                  :key="event.id"
                  class="mb-1"
                >
                  <p>
                    <i style="color: gray;">{{ $moment(event.completed_at).format('hh:mm A') }}</i>
                    <br>
                    {{ $_.get(event, 'type.name', '-') }}
                  </p>
                </li>
              </ul>
              <p v-if="$_.isEmpty(selectedDateStatusLogs) && $_.isEmpty(selectedDateEvents)">
                <i>None</i>
              </p>
            </el-card>
          </el-col>
        </el-row>
      </el-col>
      <el-col
        style="text-align: center;"
        :span="2"
      >
        <el-row
          v-for="year in years"
          :key="year"
          class="mb-2"
        >
          <el-col>
            <el-button
              size="small"
              :type="yearSelected === year ? 'primary' : ''"
              @click.native="yearSelected = year"
            >
              {{ year }}
            </el-button>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { CalendarHeatmap } from 'vue-calendar-heatmap'
import VueChart from '@/components/Chart'
export default {
  components: {
    CalendarHeatmap,
    VueChart
  },
  data () {
    return {
      years: [...Array(10).keys()].map(num => new Date().getFullYear() - num),
      yearSelected: new Date().getFullYear(),
      dateSelected: new Date(),
      events: [],
      isLoadingEvents: true,
      statusLogs: [],
      isLoadingStatusLogs: true,
      eventTypes: ['New Business Phone Call', 'New Business Appointment', 'Working On', 'Presentation', 'Decision Follow-up', 'Phone Call', 'PR', 'Carrier Setup', 'IT Setup'],
      chartOptions: {
        legend: {
          display: false
        },
        tooltips: {
          enabled: true,
          callbacks: {
            label: function (tooltipItem, data) {
              return `${data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]}%`
            }
          }
        },
        scale: {
          ticks: {
            min: -5,
            max: 100
          }
        }
      }
    }
  },
  computed: {
    currentStartDate () {
      return this.yearSelected === new Date().getFullYear() ? this.$moment(new Date()).subtract(1, 'years') : this.$moment(this.yearSelected, 'YYYY').startOf('year')
    },
    currentEndDate () {
      return this.yearSelected === new Date().getFullYear() ? new Date() : this.$moment(this.yearSelected, 'YYYY').endOf('year')
    },
    currentYearStatusLogs () {
      return this.statusLogs
        .filter(log => this.$moment(log.created_at).isSameOrAfter(this.currentStartDate) && this.$moment(log.created_at).isSameOrBefore(this.currentEndDate))
    },
    currentYearEvents () {
      return this.events
        .filter(event => event.completed_at)
        .filter(event => this.$moment(event.completed_at).isSameOrAfter(this.currentStartDate) && this.$moment(event.completed_at).isSameOrBefore(this.currentEndDate))
    },
    selectedDateStatusLogs () {
      return this.statusLogs
        .filter(log => this.$moment(log.created_at).isSame(this.dateSelected, 'day'))
    },
    selectedDateEvents () {
      return this.events
        .filter(event => event.completed_at)
        .filter(event => this.$moment(event.completed_at).isSame(this.dateSelected, 'day'))
    },
    currentEvents () {
      const currentEvents = []
      // Account Events
      this.events.filter(e => e.completed_at).map(event => {
        const existed = currentEvents.find(ce => ce.date === this.$moment(event.completed_at).format('YYYY-MM-DD'))
        if (existed) {
          existed.count++
        } else {
          currentEvents.push({
            date: this.$moment(event.completed_at).format('YYYY-MM-DD'),
            count: 1
          })
        }
      })
      // Account Status Logs
      this.statusLogs.map(log => {
        const existed = currentEvents.find(ce => ce.date === this.$moment(log.created_at).format('YYYY-MM-DD'))
        if (existed) {
          existed.count++
        } else {
          currentEvents.push({
            date: this.$moment(log.created_at).format('YYYY-MM-DD'),
            count: 1
          })
        }
      })
      return currentEvents
    },
    chartData () {
      const data = {
        labels: this.eventTypes,
        datasets: [{
          data: Array(this.eventTypes.length).fill(0),
          backgroundColor: '#2e7bcc80'
        }]
      }

      this.currentYearEvents.map(event => {
        this.eventTypes.map((type, index) => {
          if (type === this.$_.get(event, 'type.name', '-')) {
            data.datasets[0].data[index] = (1 / this.currentYearEvents.length * 100).toFixed(2)
          }
        })
      })

      return data
    }
  },
  watch: {
    yearSelected: function (newVal) {
      this.$nextTick(() => {
        this.$refs.chart.refresh()
      })
    }
  },
  created () {
    this.loadEvents()
    this.loadStatusLogs()
  },
  methods: {
    loadEvents () {
      this.isLoadingEvents = true
      this
        .$sdk
        .account_event
        .list(builder => {
          builder.context = { resource: this.$sdk.account.name, identifier: this.$route.params.accountId }
          builder.include = [ 'type', 'notes' ]
          builder.order = 'completed_at:asc'
        })
        .then(res => {
          this.events = res
        })
        .finally(() => { this.isLoadingEvents = false })
    },
    loadStatusLogs () {
      this.isLoadingStatusLogs = true
      this.$sdk.account_statuslog.list(builder => {
        builder.context = { resource: this.$sdk.account.name, identifier: this.$route.params.accountId }
        builder.order = 'created_at:asc'
      }).then(res => {
        this.statusLogs = res
      }).finally(() => { this.isLoadingStatusLogs = false })
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
